import { OkDialog } from '@/components/molecules/dialogs/OkDialog';
import { SuccessDialog } from '@/components/molecules/dialogs/SuccessDialog';
import { useAuth } from '@/core/contexts/AuthContext';
import { useTranslation } from '@/core/hooks';
import { logger } from '@/core/logger';
import { navigateToWelcomeAfterLogout } from '@/core/navigation/logoutNavigation';
import { useAppStore } from '@/core/stores/appStore';
import { CurrentPasswordScreen, PasswordSetScreen } from '@/modules/auth/components';
import { useChangePassword } from '@/modules/auth/hooks/useChangePassword';
import { usePasscode } from '@/modules/passcode';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useState } from 'react';

type ChangePasswordStep = 'current' | 'new';

export default function ChangePasswordRoute() {
    const { t } = useTranslation();
    const { login: authLogin } = useAuth();
    const { changePassword, loading, error, clearError } = useChangePassword();
    const { skipPasscode } = usePasscode();
    const params = useLocalSearchParams<{ from?: string }>();
    const [step, setStep] = useState<ChangePasswordStep>('current');
    const [currentPassword, setCurrentPassword] = useState('');
    const [errorDialogVisible, setErrorDialogVisible] = useState(false);
    const [successDialogVisible, setSuccessDialogVisible] = useState(false);

    useCatalogDynatraceView(DT_SCREEN.CHANGE_PASSWORD, 'change_password_viewed', true);

    const handleCurrentPasswordVerified = (password: string) => {
        logger.info('ChangePassword', 'Current password entered', { hasPassword: !!password });
        setCurrentPassword(password);
        setStep('new');
    };

    const handleNewPasswordSet = async (newPassword: string) => {
        logger.info('ChangePassword', 'New password entered, changing password', { 
            hasCurrentPassword: !!currentPassword 
        });

        const success = await changePassword(currentPassword, newPassword, newPassword);

        if (success) {
            logger.info('ChangePassword', 'Password changed successfully');
            setSuccessDialogVisible(true);
        } else {
            logger.error('ChangePassword', 'Failed to change password', { error });
            // Return to current password step on failure
            setCurrentPassword('');
            setStep('current');
            setErrorDialogVisible(true);
        }
    };

    const handleErrorDialogClose = () => {
        setErrorDialogVisible(false);
        clearError();
    };

    const handleSuccessDialogClose = async () => {
        setSuccessDialogVisible(false);
        // Check if password change is from profile or first-time login
        const isFromProfile = params.from === 'profile';
        
        if (isFromProfile) {
            // Password changed from profile - logout user
            logger.info('ChangePassword', 'Password changed from profile: logging out user');
            try {
                // Clear authentication state (this will clear all local data including passcode)
                await useAppStore.getState().clearAuth();
                logger.info('ChangePassword', 'User logged out after password change');
                // Navigate to welcome/login entry screen and clear stack
                await navigateToWelcomeAfterLogout();
            } catch (error) {
                logger.error('ChangePassword', 'Failed to logout after password change', error);
                // Still navigate to welcome/login entry even if logout fails
                await navigateToWelcomeAfterLogout().catch((navError) => {
                    logger.error('ChangePassword', 'Failed to navigate after logout', navError);
                });
            }
        } else {
            // First-time login flow - check passcode status from login response
            const loginResponse = useAppStore.getState().auth.loginResponse;
            const isPasscodeSet = loginResponse?.is_passcode_set === true;
            const shouldSkipPasscode = loginResponse?.skip_passcode === true;
            
            logger.info('ChangePassword', 'First-time login: checking passcode status', {
                isPasscodeSet,
                shouldSkipPasscode,
            });

            // Clear login response as we've used it
            useAppStore.getState().clearLoginResponse();

            if (shouldSkipPasscode) {
                // Skip passcode - call skip passcode API immediately, then navigate to home
                logger.info('ChangePassword', 'Skip passcode detected, calling skip passcode API');
                try {
                    await skipPasscode('login');
                    logger.info('ChangePassword', 'Skip passcode successful, navigating to home');
                    authLogin();
                    router.replace('/(auth)/(home)');
                } catch (skipError) {
                    logger.error('ChangePassword', 'Failed to skip passcode', skipError);
                    // Still navigate to home even if skip fails
                    authLogin();
                    router.replace('/(auth)/(home)');
                }
            } else if (!isPasscodeSet && !shouldSkipPasscode) {
                // Passcode not set AND skip is false - navigate to passcode setup
                logger.info('ChangePassword', 'Passcode not set and skip is false, navigating to passcode setup', {
                    isPasscodeSet,
                    shouldSkipPasscode,
                });
                authLogin();
                router.replace('/passcode?from=login&mode=setup' as any);
            } else {
                // Passcode is set - navigate to home (shouldn't happen on first login, but handle it)
                logger.info('ChangePassword', 'Passcode already set, navigating to home');
                authLogin();
                router.replace('/(auth)/(home)');
            }
        }
    };

    if (step === 'current') {
        return (
            <>
                <CurrentPasswordScreen
                    onPasswordVerified={handleCurrentPasswordVerified}
                />
                <OkDialog
                    visible={errorDialogVisible}
                    message={error || t('auth.passwordRequired')}
                    onOk={handleErrorDialogClose}
                />
            </>
        );
    }

    return (
        <>
            <PasswordSetScreen
                mode="change"
                currentPassword={currentPassword}
                onPasswordSet={handleNewPasswordSet}
                loading={loading}
            />
            <OkDialog
                visible={errorDialogVisible}
                message={error || t('errors.unknownError')}
                onOk={handleErrorDialogClose}
            />
            <SuccessDialog
                visible={successDialogVisible}
                title={t('auth.passwordChangedSuccessfully')}
                onAutoClose={handleSuccessDialogClose}
            />
        </>
    );
}

